import * as pdfjsLib from "pdfjs-dist"
import type { PageViewport, PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

class PaperReader {
  private dragging = false
  private readonly min = 35
  private readonly max = 80
  private readonly onMove = (event: MouseEvent): void => this.move(event)
  private readonly onUp = (): void => this.stop()

  constructor(private readonly element: HTMLElement) {
    this.element.querySelectorAll<HTMLElement>("[data-paper-reader-action='toggle-info']").forEach((toggle) => {
      toggle.addEventListener("click", () => this.toggleInfo())
    })

    this.element.querySelector<HTMLElement>("[data-paper-reader-split]")?.addEventListener("mousedown", (event) => {
      this.start(event)
    })
  }

  private toggleInfo(): void {
    this.element.classList.toggle("is-info-open")
  }

  private start(event: MouseEvent): void {
    event.preventDefault()
    this.dragging = true
    document.body.classList.add("is-col-resizing")
    document.addEventListener("mousemove", this.onMove)
    document.addEventListener("mouseup", this.onUp)
  }

  private move(event: MouseEvent): void {
    if (!this.dragging) return

    const rect = this.element.getBoundingClientRect()
    let pct = ((event.clientX - rect.left) / rect.width) * 100
    pct = Math.min(Math.max(pct, this.min), this.max)
    this.element.style.setProperty("--doc-basis", `${pct}%`)
  }

  private stop(): void {
    this.dragging = false
    document.body.classList.remove("is-col-resizing")
    document.removeEventListener("mousemove", this.onMove)
    document.removeEventListener("mouseup", this.onUp)
  }
}

class PdfViewer {
  private pdf: PDFDocumentProxy | null = null
  private pages: PDFPageProxy[] = []
  private wraps: HTMLElement[] = []
  private canvases: HTMLCanvasElement[] = []
  private textContainers: HTMLElement[] = []
  private textLayers: Array<pdfjsLib.TextLayer | null> = []
  private scale = window.matchMedia("(max-width: 760px)").matches ? 1.0 : 1.5
  private rendering = false
  private renderQueued = false
  private thumbsBuilt = false
  private currentPage = 1
  private observer: IntersectionObserver | null = null

  private readonly pagesEl: HTMLElement
  private readonly currentEl: HTMLInputElement
  private readonly totalEl: HTMLElement
  private readonly zoomEl: HTMLElement
  private readonly thumbsEl: HTMLElement | null
  private readonly loadingEl: HTMLElement | null
  private readonly url: string

  constructor(private readonly element: HTMLElement) {
    const pagesEl = element.querySelector<HTMLElement>("[data-pdf-pages]")
    const currentEl = element.querySelector<HTMLInputElement>("[data-pdf-current]")
    const totalEl = element.querySelector<HTMLElement>("[data-pdf-total]")
    const zoomEl = element.querySelector<HTMLElement>("[data-pdf-zoom]")

    if (!pagesEl || !currentEl || !totalEl || !zoomEl) {
      throw new Error("PDF viewer markup is incomplete.")
    }

    this.pagesEl = pagesEl
    this.currentEl = currentEl
    this.totalEl = totalEl
    this.zoomEl = zoomEl
    this.thumbsEl = element.querySelector<HTMLElement>("[data-pdf-thumbs]")
    this.loadingEl = element.querySelector<HTMLElement>("[data-pdf-loading]")
    this.url = element.dataset.pdfUrl ?? ""

    this.bindControls()
    void this.load()
  }

  destroy(): void {
    this.observer?.disconnect()
    void this.pdf?.destroy()
  }

  private bindControls(): void {
    this.element.querySelector<HTMLElement>("[data-pdf-action='toggle-thumbs']")?.addEventListener("click", () => {
      this.toggleThumbs()
    })
    this.element.querySelector<HTMLElement>("[data-pdf-action='zoom-out']")?.addEventListener("click", () => {
      void this.zoomOut()
    })
    this.element.querySelector<HTMLElement>("[data-pdf-action='zoom-in']")?.addEventListener("click", () => {
      void this.zoomIn()
    })
    this.currentEl.addEventListener("change", () => this.goToInput())
    this.currentEl.addEventListener("keydown", (event) => this.pageKeydown(event))
  }

  private async load(): Promise<void> {
    try {
      const task = pdfjsLib.getDocument({ url: this.url })
      this.pdf = await task.promise
      this.totalEl.textContent = String(this.pdf.numPages)

      for (let n = 1; n <= this.pdf.numPages; n += 1) {
        this.pages.push(await this.pdf.getPage(n))
      }

      this.shrinkToFit()
      this.buildCanvases()
      await this.renderAll()
      this.loadingEl?.remove()
      // The column can be narrower once the pages are actually in it, so
      // re-measure and correct. shrinkToFit only ever shrinks, so this settles.
      if (this.shrinkToFit()) await this.renderAll()
      this.trackCurrentPage()
    } catch (error) {
      console.error("[pdf-viewer] failed to load", error)
      if (this.loadingEl) this.loadingEl.textContent = "Couldn't load this document."
    }
  }

  private buildCanvases(): void {
    this.pagesEl.innerHTML = ""
    this.wraps = []
    this.textContainers = []
    this.textLayers = []

    this.canvases = this.pages.map((_page, index) => {
      const wrap = document.createElement("div")
      wrap.className = "pdf-page"
      wrap.dataset.page = String(index + 1)

      const canvas = document.createElement("canvas")
      canvas.className = "pdf-page__canvas"

      // Transparent, selectable copy of the page text, positioned on top of
      // the canvas. This is what makes selection, copy/paste and the browser's
      // own find-in-page work — a canvas alone has no text in the DOM.
      const text = document.createElement("div")
      text.className = "textLayer"

      wrap.append(canvas, text)
      this.pagesEl.appendChild(wrap)

      this.wraps.push(wrap)
      this.textContainers.push(text)
      this.textLayers.push(null)

      return canvas
    })
  }

  // Shrink the default scale so a page fits the column on narrow viewports.
  // Only used while loading; explicit zoom is left alone afterwards and the
  // pages scroll horizontally like any other viewer. Returns whether it moved.
  private shrinkToFit(): boolean {
    const available = this.pagesEl.clientWidth
    const base = this.pages[0]?.getViewport({ scale: 1 }).width
    if (!available || !base) return false

    const fit = available / base
    if (this.scale <= fit) return false

    this.scale = fit
    return true
  }

  private async renderAll(): Promise<void> {
    // A zoom click during a render used to be dropped, leaving the canvases
    // at a scale the toolbar no longer reported. Queue instead, so the last
    // requested scale always wins.
    if (this.rendering) {
      this.renderQueued = true
      return
    }

    this.rendering = true
    const dpr = window.devicePixelRatio || 1

    do {
      this.renderQueued = false
      const scale = this.scale

      for (let index = 0; index < this.pages.length; index += 1) {
        const page = this.pages[index]
        const viewport = page.getViewport({ scale })
        const canvas = this.canvases[index]
        const context = canvas.getContext("2d")
        if (!context) continue

        canvas.width = Math.floor(viewport.width * dpr)
        canvas.height = Math.floor(viewport.height * dpr)
        canvas.style.width = `${Math.floor(viewport.width)}px`
        canvas.style.height = `${Math.floor(viewport.height)}px`
        context.setTransform(dpr, 0, 0, dpr, 0, 0)
        await page.render({ canvas, canvasContext: context, viewport }).promise

        await this.renderText(index, viewport, scale)

        if (this.renderQueued) break
      }
    } while (this.renderQueued)

    this.rendering = false
    this.updateZoomLabel()
  }

  private updateZoomLabel(): void {
    this.zoomEl.textContent = `${Math.round(this.scale * 100)}%`
  }

  private async renderText(index: number, viewport: PageViewport, scale: number): Promise<void> {
    const container = this.textContainers[index]
    if (!container) return

    // pdf.js sizes and positions the text spans in units of this variable, so
    // updating it is all a zoom needs — the spans never have to be rebuilt.
    this.wraps[index]?.style.setProperty("--total-scale-factor", String(scale))

    const existing = this.textLayers[index]
    if (existing) {
      existing.update({ viewport })
      return
    }

    try {
      const layer = new pdfjsLib.TextLayer({
        textContentSource: await this.pages[index].getTextContent(),
        container,
        viewport,
      })
      this.textLayers[index] = layer
      await layer.render()
    } catch (error) {
      // A missing text layer only costs selection/search, so keep the page.
      console.error("[pdf-viewer] text layer failed", error)
    }
  }

  private async zoomIn(): Promise<void> {
    this.scale = Math.min(this.scale + 0.2, 3)
    await this.renderAll()
  }

  private async zoomOut(): Promise<void> {
    this.scale = Math.max(this.scale - 0.2, 0.4)
    await this.renderAll()
  }

  private toggleThumbs(): void {
    const open = this.element.classList.toggle("is-thumbs-open")
    if (open && !this.thumbsBuilt) void this.buildThumbs()
  }

  private async buildThumbs(): Promise<void> {
    if (!this.thumbsEl) return

    this.thumbsBuilt = true
    const dpr = window.devicePixelRatio || 1
    const targetWidth = 120

    for (let index = 0; index < this.pages.length; index += 1) {
      const page = this.pages[index]
      const pageNum = index + 1
      const base = page.getViewport({ scale: 1 })
      const scale = targetWidth / base.width
      const viewport = page.getViewport({ scale })

      const button = document.createElement("button")
      button.type = "button"
      button.className = "paper-doc__thumb"
      button.dataset.page = String(pageNum)
      if (pageNum === this.currentPage) button.classList.add("is-active")
      button.addEventListener("click", () => this.goToPage(pageNum))

      const canvas = document.createElement("canvas")
      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = `${Math.floor(viewport.width)}px`
      canvas.style.height = `${Math.floor(viewport.height)}px`

      const context = canvas.getContext("2d")
      if (context) {
        context.setTransform(dpr, 0, 0, dpr, 0, 0)
        await page.render({ canvas, canvasContext: context, viewport }).promise
      }

      const num = document.createElement("span")
      num.className = "paper-doc__thumb-num"
      num.textContent = String(pageNum)

      button.append(canvas, num)
      this.thumbsEl.appendChild(button)
    }
  }

  private goToInput(): void {
    const page = Number.parseInt(this.currentEl.value, 10)
    if (!Number.isNaN(page)) this.goToPage(page)
  }

  private pageKeydown(event: KeyboardEvent): void {
    if (event.key !== "Enter") return

    event.preventDefault()
    this.currentEl.blur()
    this.goToInput()
  }

  private goToPage(pageNumber: number): void {
    const page = Math.min(Math.max(pageNumber, 1), this.pages.length)
    const wrap = this.pagesEl.querySelector<HTMLElement>(`.pdf-page[data-page="${page}"]`)
    wrap?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  private trackCurrentPage(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue

          const page = Number((entry.target as HTMLElement).dataset.page)
          if (!page) continue

          this.currentPage = page
          if (document.activeElement !== this.currentEl) {
            this.currentEl.value = String(page)
          }
          this.highlightThumb(page)
        }
      },
      { root: this.pagesEl, threshold: 0.5 },
    )

    this.pagesEl.querySelectorAll(".pdf-page").forEach((el) => this.observer?.observe(el))
  }

  private highlightThumb(page: number): void {
    if (!this.thumbsEl || !this.thumbsBuilt) return

    this.thumbsEl.querySelectorAll(".paper-doc__thumb").forEach((el) => {
      const active = Number((el as HTMLElement).dataset.page) === page
      el.classList.toggle("is-active", active)
      if (active) (el as HTMLElement).scrollIntoView({ block: "nearest" })
    })
  }
}

function mountPaperReaders(): void {
  document.querySelectorAll<HTMLElement>("[data-paper-reader]").forEach((reader) => {
    new PaperReader(reader)
  })

  document.querySelectorAll<HTMLElement>("[data-pdf-viewer]").forEach((viewer) => {
    new PdfViewer(viewer)
  })
}

mountPaperReaders()
